import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import PicturePinInput from "@/components/PicturePinInput";

export default function KidSignIn() {
  const nav = useNavigate();
  const { kidLookup, kidSignIn } = useAuth();

  const [step, setStep] = useState(1); // 1 = email, 2 = pick profile, 3 = pin
  const [email, setEmail] = useState("");
  const [students, setStudents] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const lookupHouseholds = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      const list = await kidLookup(email);
      if (list.length === 0) {
        setErr("No profiles found. Ask a grown-up to set up your picture PIN first.");
      } else {
        setStudents(list);
        setStep(2);
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (pin) => {
    setErr("");
    setBusy(true);
    try {
      const res = await kidSignIn(chosen.id, pin);
      nav(`/students/${res.kid_locked_to}/home`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setErr("That is not the right picture PIN. Try again.");
      else setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen py-8 px-4"
      data-testid="kid-signin-page"
      style={{ background: "var(--tm-bg-2)" }}
    >
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <Link to="/login" className="tm-btn tm-btn-ghost" data-testid="kid-back-to-login">
            <ArrowLeft size={18} /> Grown-up sign in
          </Link>
        </div>

        <div className="tm-card">
          {step === 1 && (
            <>
              <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: "Nunito, sans-serif" }}>
                Kid sign in
              </h1>
              <p className="text-[color:var(--tm-ink-3)] mb-6">
                Ask a grown-up for their email. Then pick your picture PIN.
              </p>
              <form onSubmit={lookupHouseholds} className="space-y-5">
                <div>
                  <label htmlFor="kid-email" className="tm-label">Grown-up's email</label>
                  <input
                    id="kid-email"
                    type="email"
                    required
                    className="tm-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="kid-email-input"
                  />
                </div>
                {err && (
                  <p className="text-sm font-medium" style={{ color: "var(--tm-error)" }} data-testid="kid-error">
                    {err}
                  </p>
                )}
                <button
                  type="submit"
                  className="tm-btn tm-btn-primary w-full"
                  disabled={busy}
                  data-testid="kid-lookup-btn"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                  Next
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: "Nunito, sans-serif" }}>
                Which one is you?
              </h2>
              <p className="text-[color:var(--tm-ink-3)] mb-6">Tap your picture.</p>
              <div className="grid grid-cols-2 gap-4" data-testid="kid-profiles-grid">
                {students.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setChosen(s); setStep(3); }}
                    className="tm-card flex flex-col items-center gap-2 hover:border-[color:var(--tm-brand)] transition-colors"
                    style={{ minHeight: 140 }}
                    data-testid={`kid-profile-${s.id}`}
                  >
                    <div
                      className="rounded-full grid place-items-center text-white font-black text-2xl"
                      style={{
                        width: 68, height: 68, background: s.avatar_color || "var(--tm-brand)",
                        fontFamily: "Nunito, sans-serif",
                      }}
                      aria-hidden
                    >
                      {s.name?.[0]?.toUpperCase()}
                    </div>
                    <p className="text-xl font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>{s.name}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setStep(1); setStudents([]); }}
                className="tm-btn tm-btn-ghost mt-4"
                data-testid="kid-profiles-back"
              >
                <ArrowLeft size={16} /> Back
              </button>
            </>
          )}

          {step === 3 && chosen && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="rounded-full grid place-items-center text-white font-black text-xl"
                  style={{
                    width: 56, height: 56, background: chosen.avatar_color || "var(--tm-brand)",
                    fontFamily: "Nunito, sans-serif",
                  }}
                >
                  {chosen.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>
                    Hi {chosen.name}
                  </h2>
                  <p className="text-[color:var(--tm-ink-3)]">Tap your 3 pictures in the right order.</p>
                </div>
              </div>
              <PicturePinInput
                onComplete={submitPin}
                disabled={busy}
                testIdPrefix="kid-pin"
              />
              {err && (
                <p className="text-sm font-medium mt-3 text-center" style={{ color: "var(--tm-error)" }} data-testid="kid-pin-error">
                  {err}
                </p>
              )}
              <button
                onClick={() => { setChosen(null); setStep(2); setErr(""); }}
                className="tm-btn tm-btn-ghost mt-3"
                data-testid="kid-pin-back"
              >
                <ArrowLeft size={16} /> Pick a different profile
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
